import type { ReactNode } from 'react'
import type { Finding, Review } from '../lib/api.ts'
import {
  divergenceBrief,
  divergenceRow,
  type BriefPart,
  type BriefSide,
} from '../lib/divergenceBrief.ts'
import type { VerificationItem } from '../lib/verificationItems.ts'
import { DecisionActions, type DecisionActionsProps } from './DecisionActions.tsx'
import { Icon } from './Icon.tsx'
import type { IconName } from './icons.ts'
import { Tag } from './Tag.tsx'
import { Tooltip } from './Tooltip.tsx'
import { FOCUS_RING } from './focusRing.ts'

/**
 * One step of the divergence-only pass (#641): one divergence, top to bottom.
 *
 * The full pass is a table because it lays twenty comparisons side by side,
 * and its rows are short on purpose -- icons for answers, the sentence in a
 * tooltip. None of that is needed when the step is a single divergence, so
 * this is not that table with the rest hidden. It reads down: where it is,
 * what the receita says, what the sistema says, why the two do not agree, and
 * the two answers in words. The pin stays, small, on the receita's side.
 *
 * The card is the row the keys act on (`data-review-row`), answered or not:
 * Enter or A and Delete or X answer it, Z takes the answer back, Space pins it, and Tab and the
 * arrows move to the next divergence, as on the full pass.
 */
export interface DivergenceCardProps {
  readonly finding: Finding
  /** The pote it is on, for the ingredient line it is about. Absent on a step about none. */
  readonly item?: VerificationItem | undefined
  /** The entry it belongs to: "1) Cápsula", "Dados cadastrais". */
  readonly where: string
  readonly recorded: Review | undefined
  readonly busy: boolean
  /** Concluded, or waiting on a correction: it still reads, and asks nothing. */
  readonly readOnly: boolean
  readonly onDecision: DecisionActionsProps['onDecision']
  readonly onClearDecision: (fingerprint: string) => void
  /** The product keyed on each ingredient line, which "É excipiente" needs. */
  readonly keyedCodes?: Readonly<Record<string, string>> | undefined
  readonly onMarkExcipient?:
    | ((ingredientId: string, productName: string, fingerprints: readonly string[]) => void)
    | undefined
  /** Whether the divergence is held on the prescription. The step opens with it held. */
  readonly pinned?: boolean
  /** Holds it on the prescription, or lets it go. */
  readonly onTogglePin?: (() => void) | undefined
}

/** How the card is drawn: what is still open, what could not be compared, what is answered. */
type Tone = 'divergence' | 'undecided' | 'answered'

const FRAME: Record<Tone, string> = {
  divergence: 'border-stroke-error-weak',
  undecided: 'border-stroke-warning-weak',
  answered: 'border-stroke-weak',
}

const BAND: Record<Tone, string> = {
  divergence: 'bg-fill-error-weak border-stroke-error-weak',
  undecided: 'bg-fill-warning-weak border-stroke-warning-weak',
  answered: 'bg-fill-weak border-stroke-weak',
}

/** The painted part of a side: what differs, in the colour of what was found. */
const PAINT: Record<Tone, string> = {
  divergence: 'var(--color-text-error)',
  undecided: 'var(--color-text-warning)',
  answered: 'var(--color-text-error)',
}

export function DivergenceCard({
  finding,
  item,
  where,
  recorded,
  busy,
  readOnly,
  onDecision,
  onClearDecision,
  keyedCodes = {},
  onMarkExcipient,
  pinned = false,
  onTogglePin,
}: DivergenceCardProps) {
  const brief = divergenceBrief(finding, item)
  const row = item === undefined ? undefined : divergenceRow(item, finding)
  const answered = recorded !== undefined
  const undecided = finding.disposition === 'undecided'
  const tone: Tone = answered ? 'answered' : undecided ? 'undecided' : 'divergence'
  // What the page has for it: the line's own blocks, else what the finding cited.
  const located = (row?.regions ?? finding.regions).length > 0
  /*
   * On the receita's side when there is one; a rule has none, so on its first
   * line. Only where the page has the divergence to hold.
   */
  const pin =
    onTogglePin !== undefined && located ? (
      <Pin on={pinned} flagged={tone === 'divergence'} onToggle={onTogglePin} />
    ) : undefined

  // "É excipiente", only on a keyed line the receita never named, as on the full pass.
  const keyed = row?.keyedIngredientId
  const product = row?.quotedName
  const onExcipient =
    !readOnly &&
    !busy &&
    onMarkExcipient !== undefined &&
    keyed !== undefined &&
    product !== undefined &&
    keyedCodes[keyed] !== undefined
      ? (fingerprints: readonly string[]) => onMarkExcipient(keyed, product, fingerprints)
      : undefined

  return (
    <article
      data-review-row={finding.fingerprint}
      // Always a stop of the keys, answered or not: it is the whole step.
      data-question={readOnly ? undefined : 'true'}
      data-needs-decision={!readOnly && !answered ? 'true' : 'false'}
      tabIndex={0}
      aria-label={`Divergência: ${brief.field}`}
      className={`rounded-base bg-background-raised shadow-raised flex flex-col overflow-hidden border ${FRAME[tone]} ${FOCUS_RING}`}
      onKeyDown={(event) => {
        // Space holds it on the prescription, or lets it go, as on a row of the full pass.
        if (
          event.key === ' ' &&
          event.target === event.currentTarget &&
          onTogglePin !== undefined
        ) {
          event.preventDefault()
          onTogglePin()
        }
      }}
    >
      <header
        className={`flex flex-wrap items-start justify-between gap-x-3 gap-y-2 border-b px-5 py-4 ${BAND[tone]}`}
      >
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-tSmall text-text-weak break-words">{where}</span>
          <h3 className="text-tH5 text-text-strong m-0 break-words">{brief.field}</h3>
        </div>
        <Tag tone={answered ? 'success' : undecided ? 'warning' : 'danger'}>
          {answered ? 'Respondida' : undecided ? 'Não comparado' : 'Em aberto'}
        </Tag>
      </header>

      <div className="flex flex-col gap-5 px-5 py-5">
        {brief.sides !== undefined && (
          <div className="flex flex-col">
            <SideBlock side={brief.sides[0]} icon="prescription" paint={PAINT[tone]} action={pin} />
            <Between tone={tone} />
            <SideBlock side={brief.sides[1]} icon="system" paint={PAINT[tone]} />
          </div>
        )}

        {brief.ingredients !== undefined && (
          <div className="flex flex-col gap-1.5">
            <Caption action={brief.sides === undefined ? pin : undefined}>
              Ativos envolvidos
            </Caption>
            <div className="flex flex-wrap gap-1.5">
              {brief.ingredients.map((name) => (
                <Tag key={name} tone="neutral" variant="stroke">
                  {name}
                </Tag>
              ))}
            </div>
          </div>
        )}

        {brief.reason !== undefined && (
          <div className="flex flex-col gap-1">
            <Caption
              action={
                brief.sides === undefined && brief.ingredients === undefined ? pin : undefined
              }
            >
              {brief.sides === undefined ? 'O que a regra diz' : 'Por que'}
            </Caption>
            <p className="text-tP text-text-strong m-0 break-words">{brief.reason}</p>
          </div>
        )}
      </div>

      {!readOnly && (
        <footer className="border-stroke-weak border-t px-5 py-4">
          <DecisionActions
            labelled
            finding={finding}
            recorded={recorded}
            busy={busy}
            onDecision={onDecision}
            onClear={onClearDecision}
            onExcipient={onExcipient}
            rowName={row?.name}
          />
        </footer>
      )}
    </article>
  )
}

/**
 * The pin of the full pass's rows, on the receita's side: faint until the
 * divergence is held on the prescription, then in the colour of what was
 * found. The words are in the tooltip.
 */
function Pin({ on, flagged, onToggle }: { on: boolean; flagged: boolean; onToggle: () => void }) {
  return (
    <Tooltip text={on ? 'Fixado na receita — clique para desafixar' : 'Fixar na receita'}>
      <button
        type="button"
        // The card is the stop; Space presses this.
        tabIndex={-1}
        aria-label={on ? 'Desafixar da receita' : 'Fixar na receita'}
        aria-pressed={on}
        onClick={onToggle}
        className={`inline-flex size-6 cursor-pointer items-center justify-center rounded-[5px] border-none bg-transparent text-xs transition-opacity ${FOCUS_RING} ${
          on
            ? flagged
              ? 'text-icon-error opacity-100'
              : 'text-icon-brand opacity-100'
            : 'text-icon-neutral opacity-40 hover:opacity-100'
        }`}
      >
        <Icon name="pin" />
      </button>
    </Tooltip>
  )
}

function Caption({ children, action }: { children: string; action?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-tSmall text-text-weak uppercase tracking-[0.04em]">{children}</span>
      {action}
    </div>
  )
}

/** One side, said whole: its heading, then the value, its differing part painted. */
function SideBlock({
  side,
  icon,
  paint,
  action,
}: {
  side: BriefSide
  icon: IconName
  paint: string
  action?: ReactNode
}) {
  // A posology is prose; a name or a dose is something to read at a glance.
  const prose = side.parts.some((part) => part.text.length > 60 || part.text.includes('\n'))
  return (
    <section
      aria-label={side.heading}
      className="rounded-base border-stroke-weak bg-fill-weaker flex flex-col gap-1.5 border px-4 py-3"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-tSmall text-text-weak flex items-center gap-1.5 uppercase tracking-[0.04em]">
          <Icon name={icon} />
          {side.heading}
        </span>
        {action}
      </div>
      {side.parts.length === 0 ? (
        <p className="text-tP text-text-weak m-0 italic">Não consta</p>
      ) : (
        <p
          className={`text-text-strong m-0 break-words whitespace-pre-line ${prose ? 'text-tP' : 'text-tH6'}`}
        >
          {side.parts.map((part, index) => (
            <span key={index}>
              {index > 0 && <span className="text-text-weak"> · </span>}
              <Painted part={part} paint={paint} />
            </span>
          ))}
        </p>
      )}
    </section>
  )
}

function Painted({ part, paint }: { part: BriefPart; paint: string }) {
  const mark = (text: string, key?: number) => (
    <mark
      key={key}
      data-differs="true"
      className={`rounded-[3px] px-0.5 ${part.absent === true ? 'italic' : ''}`}
      style={{
        color: paint,
        background: `color-mix(in srgb, ${paint} 14%, transparent)`,
      }}
    >
      {text}
    </mark>
  )
  if (part.letters !== undefined) {
    return (
      <>
        {part.letters.map((segment, index) =>
          segment.differs ? mark(segment.text, index) : <span key={index}>{segment.text}</span>,
        )}
      </>
    )
  }
  return part.differs ? mark(part.text) : <>{part.text}</>
}

/** Where the two sides meet: they do not agree, or they could not be compared. */
function Between({ tone }: { tone: Tone }) {
  return (
    <div aria-hidden="true" className="relative h-3">
      <span
        className="bg-background-raised border-stroke-weak absolute top-1/2 left-1/2 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border text-xs"
        style={{ color: PAINT[tone] }}
      >
        <Icon name={tone === 'undecided' ? 'undecided' : 'notEqual'} />
      </span>
    </div>
  )
}
